import React, { useState, useEffect } from 'react';
import { Clock, Play, CheckCircle, AlertCircle, User } from 'lucide-react';
import { bailAgentService } from '../../../services/bailAgentService';
import Button from '../../../components/ui/Button';

const AgentShiftManager = () => {
  const [agents, setAgents] = useState([]);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all agents
      const allAgentsResult = await bailAgentService?.getAllAgents();
      
      if (allAgentsResult?.error) {
        setError(allAgentsResult?.error);
        return;
      }

      // Fetch current agent
      const currentAgentResult = await bailAgentService?.getCurrentAgent();
      
      setAgents(allAgentsResult?.data || []);
      setCurrentAgent(currentAgentResult?.data || null);
      
    } catch (err) {
      setError('Failed to load agents data');
    } finally {
      setLoading(false);
    }
  };

  const handleSetOnShift = async (agentId, agentName) => {
    setUpdating(agentId);
    setError(null);
    
    try {
      const result = await bailAgentService?.setAgentOnShift(agentId);
      
      if (result?.error) {
        setError(result?.error);
        return;
      }

      // Refresh data after successful shift change
      await fetchData();
      
      // Show success message
      alert(`${agentName} is now on shift. All homepage agent information has been updated.`);
      
    } catch (err) {
      setError('Failed to update agent shift');
    } finally {
      setUpdating(null);
    }
  };

  const formatShiftTime = (shiftStarted) => {
    if (!shiftStarted) return '';
    const start = new Date(shiftStarted);
    const now = new Date();
    const diffHours = Math.floor((now - start) / (1000 * 60 * 60));
    const diffMins = Math.floor((now - start) / (1000 * 60)) % 60;
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMins}m`;
    }
    return `${diffMins}m`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Agent Shift Management</h3>
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span>Loading agents...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Agent Shift Management</h3>
        <p className="text-sm text-gray-600 mt-1">
          Manage bail agent shifts. When you set an agent on shift, all homepage information updates automatically.
        </p>
      </div>

      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-red-800">Error</h4>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Current Agent on Shift */}
      {currentAgent && (
        <div className="mx-6 mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-800">Currently On Shift</span>
              </div>
              <div className="text-sm text-emerald-700">
                <strong>{currentAgent?.full_name}</strong>
                {currentAgent?.shift_started_at && (
                  <span className="ml-2">
                    (On duty for {formatShiftTime(currentAgent?.shift_started_at)})
                  </span>
                )}
              </div>
            </div>
            <div className="text-xs text-emerald-600 bg-emerald-100 px-2 py-1 rounded">
              Active on Homepage
            </div>
          </div>
        </div>
      )}

      <div className="p-6">
        <div className="grid gap-4">
          {agents?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No bail agents found</p>
              <p className="text-sm mt-1">Add agents first to manage shifts</p>
            </div>
          ) : (
            agents?.map((agent) => (
              <div 
                key={agent?.id}
                className={`border rounded-lg p-4 transition-colors ${
                  agent?.is_currently_on_shift 
                    ? 'border-emerald-200 bg-emerald-50' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Agent Avatar */}
                    <div className="flex-shrink-0">
                      {agent?.profile_photo_url ? (
                        <img
                          src={agent?.profile_photo_url}
                          alt={agent?.full_name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold">
                            {agent?.full_name?.split(' ')?.map(n => n?.[0])?.join('') || '?'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Agent Info */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h4 className="font-semibold text-gray-900">{agent?.full_name}</h4>
                        <span className="text-sm text-gray-600">License: {agent?.license_number}</span>
                        <span className="text-sm text-gray-600">{agent?.phone}</span>
                        {agent?.is_currently_on_shift && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                            <div className="w-2 h-2 bg-emerald-400 rounded-full mr-1"></div>
                            On Shift
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                        <span>Experience: {agent?.years_experience || 0} years</span>
                        <span>Status: {agent?.status}</span>
                        {agent?.specializations?.length > 0 && (
                          <span>Specializations: {agent?.specializations?.slice(0, 2)?.join(', ')}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Shift Controls */}
                  <div className="flex items-center space-x-2">
                    {agent?.is_currently_on_shift ? (
                      <div className="flex items-center space-x-2 text-emerald-700">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {formatShiftTime(agent?.shift_started_at)}
                        </span>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleSetOnShift(agent?.id, agent?.full_name)}
                        disabled={updating === agent?.id}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {updating === agent?.id ? (
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Setting...</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <Play className="w-4 h-4" />
                            <span>Set On Shift</span>
                          </div>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <AlertCircle className="w-4 h-4 text-blue-600" />
          <p>
            <strong>Note:</strong> Setting an agent on shift automatically updates all homepage information including 
            the Emergency Support section and agent container with the new agent's details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgentShiftManager;